import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import URI from 'urijs';
import uuid from 'uuid/v4';
import { Spinner } from 'components/common';
import connect from 'stores/connect';
import Store from 'logic/local-storage/Store';
import {
  LOGIN_INITIALIZING_STATE,
  LOGIN_TRANSITIONING_STATE,
  LOGIN_INITIALIZED_STATE,
} from 'logic/authentication/constants';

import { OKTA_TYPE_KEY } from 'authentication/components/oidc/constants';

import OidcCallback from './OidcCallback';
import { OidcConfigurationActions, OidcConfigurationStore } from './OidcConfigurationStore';
import { OidcSessionStore } from './OidcSessionStore';
import OidcGenericLoginLink from './OidcGenericLoginLink';

import OktaLoginForm from '../okta/OktaLoginForm';

const OidcLogin = ({ configuration, isLoading, onErrorChange, setLoginFormState }) => {
  const uri = new URI(window.location.href);

  useEffect(() => {
    OidcConfigurationActions.getConfig();
  }, []);

  const isCallbackUrl = () => {
    return uri.path() === '/authorization-code/callback';
  };

  const generateNonce = (redirectTo = uri.resource()) => {
    return {
      state: uuid(),
      redirectTo: redirectTo,
    };
  };

  const [nonce, setNonce] = useState(() => {
    try {
      if (isCallbackUrl()) {
        return Store.sessionGet('nonce');
      }
    } catch (e) {
      return generateNonce();
    }

    return generateNonce();
  });

  const regenerateNonce = () => {
    setNonce((prevNonce) => {
      return generateNonce(prevNonce.redirectTo);
    });
  };

  useEffect(() => {
    Store.sessionSet('nonce', nonce);
  }, [nonce]);

  if (isLoading || !configuration) {
    setLoginFormState(LOGIN_INITIALIZING_STATE);

    return (
      <p className="loading-text">
        <Spinner text="Loading, please wait..." delay={0} />
      </p>
    );
  }

  if (isCallbackUrl()) {
    setLoginFormState(LOGIN_TRANSITIONING_STATE);

    return (
      <OidcCallback configuration={configuration}
                    nonce={nonce}
                    onErrorChange={onErrorChange}
                    params={uri.query(true)}
                    regenerateNonce={regenerateNonce} />
    );
  }

  setLoginFormState(LOGIN_INITIALIZED_STATE);

  return configuration.oauth_type === OKTA_TYPE_KEY ? (
    <OktaLoginForm checkSession
                   configuration={configuration}
                   nonce={nonce}
                   onErrorChange={onErrorChange} />
  )
    : <OidcGenericLoginLink configuration={configuration} nonce={nonce} />;
};

OidcLogin.propTypes = {
  configuration: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  onErrorChange: PropTypes.func.isRequired,
  setLoginFormState: PropTypes.func.isRequired,
};

OidcLogin.defaultProps = {
  configuration: undefined,
};

export default connect(OidcLogin, {
  configuration: OidcConfigurationStore,
  oidcSession: OidcSessionStore,
},
({ configuration, oidcSession, ...props }) => ({
  ...props,
  configuration: configuration.configuration,
  isLoading: oidcSession.isLoading,
}));
